/* =====================================================================
   YMM (Year/Make/Model) data layer
   ---------------------------------------------------------------------
   Phase 1 / 2: reads from the static placeholder JSON in assets/.
   Phase 8 (or sooner): swap getFitment() to call the real fitment DB API.
   The function signature and return shape are the contract — every
   consumer (PDP fitment checker, homepage YMM widget) calls getFitment()
   and works with the same record format.

   Record shape (matches Shopify custom.fitment metafield exactly):
     [{ yearStart, yearEnd, makes: [], models: [] }]

   Helpers built on top of getFitment() expose what UIs actually need:
     - listYears()     → sorted unique years across all records
     - listMakes(year) → makes available for a given year
     - listModels(year, make) → models available for year+make
     - matches(vehicle, ranges) → does a selected vehicle satisfy any range?
   ===================================================================== */

(function () {
  'use strict';

  let _cache = null;
  let _inflight = null;
  let _source = null;

  // ------------------------------------------------------------------
  // configure() — called once with the placeholder URL by the page that
  // mounts the YMM widget. Phase 8 will replace this implementation.
  // ------------------------------------------------------------------
  function configure(opts) {
    _source = opts && opts.source;
  }

  // ------------------------------------------------------------------
  // getFitment() — async, returns array of range records.
  // Cached after first load; safe to call from multiple consumers.
  // ------------------------------------------------------------------
  async function getFitment() {
    if (_cache) return _cache;
    if (_inflight) return _inflight;
    if (!_source) {
      console.warn('[ymm] getFitment called before configure() — call window.HHymm.configure({ source: "..." }) first');
      return [];
    }
    _inflight = fetch(_source, { credentials: 'omit' })
      .then(r => r.ok ? r.json() : Promise.reject(new Error('YMM source ' + r.status)))
      .then(data => {
        _cache = Array.isArray(data) ? data : [];
        _inflight = null;
        return _cache;
      })
      .catch(err => {
        console.warn('[ymm] failed to load fitment:', err);
        _cache = [];
        _inflight = null;
        return _cache;
      });
    return _inflight;
  }

  // ------------------------------------------------------------------
  // Derived helpers
  // ------------------------------------------------------------------
  async function listYears() {
    const ranges = await getFitment();
    const years = new Set();
    for (const r of ranges) {
      const start = parseInt(r.yearStart, 10);
      const end = parseInt(r.yearEnd, 10);
      if (Number.isFinite(start) && Number.isFinite(end)) {
        for (let y = start; y <= end; y++) years.add(y);
      }
    }
    return Array.from(years).sort((a, b) => b - a); // newest first
  }

  async function listMakes(year) {
    const ranges = await getFitment();
    const makes = new Set();
    const yr = year ? parseInt(year, 10) : null;
    for (const r of ranges) {
      if (yr && (yr < parseInt(r.yearStart, 10) || yr > parseInt(r.yearEnd, 10))) continue;
      for (const m of (r.makes || [])) makes.add(m);
    }
    return Array.from(makes).sort();
  }

  async function listModels(year, make) {
    const ranges = await getFitment();
    // Dedupe case-insensitively so the Super Duty collapse (F-250 + F-250 Super
    // Duty → one "F-250 Super Duty" option for 2000+) yields a single entry and
    // we stop generating the picker mismatch at the source. superDutyCanonicalLabel
    // is a no-op for ≤ 1999 / no-year / non-Ford, so those keep both variants.
    const byKey = new Map();
    const yr = year ? parseInt(year, 10) : null;
    for (const r of ranges) {
      if (yr && (yr < parseInt(r.yearStart, 10) || yr > parseInt(r.yearEnd, 10))) continue;
      if (make && !(r.makes || []).some(m => m.toLowerCase() === make.toLowerCase())) continue;
      for (const m of (r.models || [])) {
        const label = superDutyCanonicalLabel(m, yr, make);
        const key = label.toLowerCase();
        if (!byKey.has(key)) byKey.set(key, label);
      }
    }
    return Array.from(byKey.values()).sort();
  }

  // ------------------------------------------------------------------
  // Ford Super Duty model normalization — YEAR-GATED (spec 260716).
  //
  // Ford split the Super Duty off the F-Series mid-cycle, so "F-250" means
  // different trucks by year:
  //   1997–1998  Light Duty only (F-150-based); Super Duty didn't exist
  //   1999       BOTH exist (final-year LD F-250 *and* first-year SD F-250)
  //   2000+      Super Duty only (LD discontinued)
  //
  // So for model-year ≥ 2000 (and ONLY then) the picker/data variants
  // "F-250" and "F-250 Super Duty" (+ F-350/450/550) name the SAME truck and
  // must match interchangeably. For ≤ 1999 they must NOT — a blanket synonym
  // would false-positive 1997–1999 Light-Duty parts, and 1999 is genuinely
  // ambiguous (data must be explicit, not bridged by this rule).
  //
  // The set is ENUMERATED exactly (not fuzzy-contains): "F-250 HD" (pre-SD
  // heavy-duty) and "F-250 Heritage" contain "F-250" but are distinct trucks,
  // so they fall through unchanged and never collapse into the SD class.
  //
  // superDutyCanonicalLabel(model, year, make) → the canonical display label
  // ("F-250 Super Duty") when `model` is in the SD equivalence class for a
  // Ford of year ≥ 2000, otherwise the ORIGINAL model string unchanged.
  // ------------------------------------------------------------------
  const SD_LINES = ['250', '350', '450', '550'];

  function superDutyCanonicalLabel(model, year, make) {
    const original = model == null ? '' : String(model);
    if ((make || '').toLowerCase() !== 'ford') return original;
    const yr = parseInt(year, 10);
    if (!Number.isFinite(yr) || yr < 2000) return original;
    const md = original.toLowerCase().replace(/\s+/g, ' ').trim();
    for (const n of SD_LINES) {
      if (md === 'f-' + n || md === 'f-' + n + ' super duty') return 'F-' + n + ' Super Duty';
    }
    return original;
  }

  // ------------------------------------------------------------------
  // Make-prefixed model spelling — MAKE-GATED to Ram (GH #2985).
  //
  // Our custom.fitment spells Ram trucks `Ram | Ram 1500` (Steve ruled that
  // spelling canonical, 2026-10-16); the oracle this picker reads
  // (ymm-fitment.json) spells the same truck `Ram | 1500`. Exact string
  // compare made every such cell "no fit" for a buyer who picked Ram / 1500.
  //
  // THE RULE IS THE eBay PUSH'S, not a second one: `normalizeToOracleVocabulary`
  // in src/utils/push-ebay-compatibility.js strips the leading make token when
  // the model begins with `<make><space>` (lowercased compare, then
  // `model.slice(make.length + 1).trim()`), gated by the oracle: (1) the pair
  // as spelled is ABSENT from the oracle and (2) the stripped pair is PRESENT.
  // Here the gates reduce to a make allowlist, because:
  //   (1) the oracle carries no `Ram | Ram …` pair at all — asserted against
  //       the real ymm-fitment.json by tests/unit/ymm-ram-model-alias.test.js,
  //       which reds the day the oracle gains one;
  //   (2) the other side of every compare is the buyer's pick, which IS an
  //       oracle pair, so "stripped pair present" is the equality itself.
  // The only oracle pair of this shape is `Nissan | Nissan Z`, a real model the
  // push's gate (1) leaves alone — which is why the allowlist is not "every make".
  //
  // Dodge is NOT in the list: the oracle spells Dodge trucks `Dodge | Ram 1500`
  // (no prefix to strip; they already match exactly), and the push's rule never
  // fires on them either (the model does not begin with "dodge ").
  //
  // EXACT prefix strip only: `Ram 1500` → `1500`, never `1500 Classic`;
  // `Ram 3500` → `3500`, never `1500`; `ProMaster 1500` is untouched.
  // ------------------------------------------------------------------
  const MAKE_PREFIXED_MAKES = ['ram'];

  function oracleModelSpelling(model, make) {
    const original = model == null ? '' : String(model);
    const mk = String(make || '').trim().toLowerCase();
    if (MAKE_PREFIXED_MAKES.indexOf(mk) === -1) return original;
    if (!original.toLowerCase().startsWith(mk + ' ')) return original;
    const stripped = original.slice(mk.length + 1).trim();
    return stripped || original;
  }

  // The fitment_models FACET values that name one picked vehicle: the oracle
  // spelling, plus our make-prefixed spelling for an allowlisted make. Shopify
  // ORs repeated values of one filter param (measured live 2026-10-16:
  // fitment_models=1500 → 1 result, =Ram 1500 → 17, both → 18), so a URL that
  // carries both reaches the listings under either spelling.
  function modelFilterValues(make, model) {
    const original = model == null ? '' : String(model).trim();
    if (!original) return [];
    const bare = oracleModelSpelling(original, make);
    const out = [original];
    if (bare !== original) out.push(bare);
    else if (MAKE_PREFIXED_MAKES.indexOf(String(make || '').trim().toLowerCase()) !== -1) {
      out.push(String(make).trim() + ' ' + original);
    }
    return out;
  }

  // ------------------------------------------------------------------
  // matches(vehicle, ranges) — pure function, no fetch.
  // Used by the PDP fitment checker against product.metafields.custom.fitment
  // (already inlined into the page; doesn't need to call getFitment()).
  //
  // vehicle: { year, make, model }
  // ranges:  array of { yearStart, yearEnd, makes, models } (e.g., custom.fitment)
  // returns true if vehicle satisfies any range
  // ------------------------------------------------------------------
  function matches(vehicle, ranges) {
    if (!vehicle || !ranges || !ranges.length) return false;
    const yr = parseInt(vehicle.year, 10);
    const mk = (vehicle.make || '').toLowerCase();
    const md = (vehicle.model || '').toLowerCase();
    // Canonicalize the picked model once. The year gate lives inside the
    // canonicalizer, so this is a no-op for ≤ 1999 / non-Ford picks; the
    // make-prefix strip is a no-op for every make but Ram (GH #2985).
    const pickedCanon = superDutyCanonicalLabel(oracleModelSpelling(vehicle.model, vehicle.make), yr, vehicle.make).toLowerCase();
    for (const r of ranges) {
      const start = parseInt(r.yearStart, 10);
      const end = parseInt(r.yearEnd, 10);
      if (!Number.isFinite(yr) || yr < start || yr > end) continue;
      const makeOk = !mk || (r.makes || []).some(m => m.toLowerCase() === mk);
      if (!makeOk) continue;
      // Compare each range model canonicalized at the SAME picked year+make, so
      // a data value of "F-250" and a pick of "F-250 Super Duty" (2000+) match,
      // and a data value of "Ram 1500" and a pick of Ram / "1500" match.
      const modelOk = !md || !(r.models || []).length ||
        (r.models || []).some(m => superDutyCanonicalLabel(oracleModelSpelling(m, vehicle.make), yr, vehicle.make).toLowerCase() === pickedCanon);
      if (modelOk) return true;
    }
    return false;
  }

  // ------------------------------------------------------------------
  // resolveVerdict(vehicle, ranges, exclusions) — pure, no fetch, no DOM.
  //
  // The buy-box "check-fit verdict" state machine (Storefront-audit Leg B).
  // Implements the LOCKED ✅/⚠️/❌ machine from theme CLAUDE.md
  // ("Vehicle banner exclusion logic"), including the fitment_exclusions
  // trim-disambiguation the flat matches() alone cannot express.
  //
  //   universal_fitment == "Yes"  → handled SERVER-SIDE (never reaches here)
  //
  //   no vehicle stored                                   → 'no-vehicle'
  //   YMM does not match                                  → 'no-fit'
  //   YMM matches, no exclusions                          → 'fits'         ✅
  //   YMM matches, has exclusions, trim UNKNOWN           → 'verify'       ⚠️
  //   YMM matches, has exclusions, trim = an excluded key → 'excluded'     ❌
  //   YMM matches, has exclusions, trim = non-excluded    → 'fits'         ✅
  //
  // vehicle:    { year, make, model, trim? } or null. `trim` is the buyer's
  //             disclosed trim/drivetrain (e.g. 'hybrid'), set via the
  //             one-question disambiguator through the HHymm funnel.
  // ranges:     product.metafields.custom.fitment (array of range objects)
  // exclusions: product.metafields.custom.fitment_exclusions (list of keys,
  //             e.g. ['hybrid']); today the only value in the catalog.
  //
  // Returns { state, label, exclusions?, exclusion? }.
  //   label     = "YYYY MAKE MODEL" (for copy)
  //   exclusions= normalized non-empty exclusion keys (on 'verify')
  //   exclusion = the specific excluded trim the buyer disclosed (on 'excluded')
  // ------------------------------------------------------------------
  //   vehicle stored, the part carries NO fitment data    → 'no-data'      (neutral, GH #3433)
  //     — "we have not listed its fit", which is not "it does not fit". Before
  //       2026-11-01 the PDP rendered nothing here and the cart skipped the line.
  function resolveVerdict(vehicle, ranges, exclusions) {
    if (!vehicle) return { state: 'no-vehicle' };
    const label = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ');

    if (!Array.isArray(ranges) || !ranges.length) return { state: 'no-data', label };

    if (!matches(vehicle, ranges)) return { state: 'no-fit', label };

    const excl = (Array.isArray(exclusions) ? exclusions : [])
      .map(e => String(e || '').trim().toLowerCase())
      .filter(Boolean);
    if (!excl.length) return { state: 'fits', label };

    const trim = String(vehicle.trim || '').trim().toLowerCase();
    if (!trim) return { state: 'verify', label, exclusions: excl };

    if (excl.indexOf(trim) !== -1) return { state: 'excluded', label, exclusion: trim };
    return { state: 'fits', label };
  }

  // ------------------------------------------------------------------
  // verdictCopy(verdict) — pure. THE wording for a verdict, shared by the
  // PDP buy box and the cart line (GH #3433), so the two cannot disagree.
  // Plain text; the caller escapes. Returns null for 'no-vehicle'.
  //   tone: 'fits' (green) · 'verify' (amber) · 'no-fit' (red) · 'neutral'
  //         (no claim either way). The PDP and cart map a tone to the same palette.
  //   link: optional { text, href } — a substring of headline the caller links.
  // ------------------------------------------------------------------
  function exclusionText(keys) {
    return (keys || []).map(function (k) {
      k = String(k || '');
      return k.charAt(0).toUpperCase() + k.slice(1);
    }).join(' / ');
  }

  function verdictCopy(verdict) {
    if (!verdict || verdict.state === 'no-vehicle') return null;
    const label = verdict.label || '';
    switch (verdict.state) {
      case 'fits':
        return { tone: 'fits', headline: 'Fits your ' + label };
      case 'verify':
        return { tone: 'verify', headline: 'Verify your trim — does not fit ' + exclusionText(verdict.exclusions) + ' models' };
      case 'excluded':
        return { tone: 'no-fit', headline: 'Does not fit ' + exclusionText([verdict.exclusion]) + ' models' };
      case 'no-fit':
        return { tone: 'no-fit', headline: 'Doesn’t fit your ' + label };
      case 'no-data':
        return {
          tone: 'neutral',
          headline: 'Fit not listed for this part — message Hubes with your vehicle',
          link: { text: 'message Hubes', href: '/pages/contact' },
        };
      default:
        return null;
    }
  }

  // ------------------------------------------------------------------
  // Vehicle persistence — localStorage per YMM doc
  // ------------------------------------------------------------------
  const STORAGE_KEY = 'hh-ymm-vehicle';

  function getStoredVehicle() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); }
    catch (e) { return null; }
  }

  function setStoredVehicle(v) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(v)); } catch (e) {}
    document.dispatchEvent(new CustomEvent('hh:vehicle-changed', { detail: v }));
  }

  function clearStoredVehicle() {
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
    document.dispatchEvent(new CustomEvent('hh:vehicle-changed', { detail: null }));
  }

  // ------------------------------------------------------------------
  // Public API
  // ------------------------------------------------------------------
  window.HHymm = {
    configure: configure,
    getFitment: getFitment,
    listYears: listYears,
    listMakes: listMakes,
    listModels: listModels,
    matches: matches,
    superDutyCanonicalLabel: superDutyCanonicalLabel,
    oracleModelSpelling: oracleModelSpelling,
    modelFilterValues: modelFilterValues,
    resolveVerdict: resolveVerdict,
    verdictCopy: verdictCopy,
    getStoredVehicle: getStoredVehicle,
    setStoredVehicle: setStoredVehicle,
    clearStoredVehicle: clearStoredVehicle,
  };
})();
